import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE, GREEN] = CHART_COLORS;

/** One decimal: log and root results can differ in the last digit between Node and a browser. */
const round = (value: number): number => Math.round(value * 10) / 10;

const PLOT = { left: 64, right: 530, top: 22, bottom: 206 };
const Y_TICKS = [1, 2, 5, 10, 20, 50] as const;
const Y_MIN = 1;
const Y_MAX = 50;
const X_TICKS = [0, 0.2, 0.4, 0.6, 0.8, 1] as const;

function x(beta: number): number {
  return round(PLOT.left + beta * (PLOT.right - PLOT.left));
}

function y(gamma: number): number {
  const clamped = Math.min(Math.max(gamma, Y_MIN), Y_MAX);
  const span = Math.log(Y_MAX) - Math.log(Y_MIN);
  return round(
    PLOT.bottom -
      ((Math.log(clamped) - Math.log(Y_MIN)) / span) * (PLOT.bottom - PLOT.top)
  );
}

const CURVE = (() => {
  const points: string[] = [];
  // γ reaches the top of the plot near β = 0.9998.
  for (let i = 0; i <= 200; i++) {
    const beta = (i / 200) * 0.9998;
    const gamma = 1 / Math.sqrt((1 - beta) * (1 + beta));
    points.push(`${x(beta)},${y(gamma)}`);
  }
  return points.join(" ");
})();

/** γ against β on a log axis, with the reader's speed marked. */
export function GammaCurve({
  id,
  label,
  description,
  beta,
  gamma,
  markerText,
}: {
  id: string;
  label: string;
  description: string;
  beta: number;
  gamma: number;
  /** The text beside the marker, e.g. "γ = 1.667". */
  markerText: string;
}) {
  const px = x(Math.min(beta, 0.9999));
  const py = y(gamma);
  return (
    <ChartSvg
      description={description}
      height={250}
      id={id}
      label={label}
      width={560}
    >
      {Y_TICKS.map((tick) => (
        <g key={tick}>
          <line
            stroke={CHART_INK.grid}
            x1={PLOT.left}
            x2={PLOT.right}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            fill={CHART_INK.muted}
            fontSize="12"
            textAnchor="end"
            x={PLOT.left - 8}
            y={y(tick) + 4}
          >
            {tick}
          </text>
        </g>
      ))}
      {X_TICKS.map((tick) => (
        <text
          fill={CHART_INK.muted}
          fontSize="12"
          key={tick}
          textAnchor="middle"
          x={x(tick)}
          y={PLOT.bottom + 18}
        >
          {tick}
        </text>
      ))}
      <line
        stroke={CHART_INK.axis}
        x1={PLOT.left}
        x2={PLOT.right}
        y1={PLOT.bottom}
        y2={PLOT.bottom}
      />
      <line
        stroke={CHART_INK.axis}
        x1={PLOT.left}
        x2={PLOT.left}
        y1={PLOT.top}
        y2={PLOT.bottom}
      />
      <polyline fill="none" points={CURVE} stroke={BLUE} strokeWidth="3" />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="4 3"
        x1={px}
        x2={px}
        y1={py}
        y2={PLOT.bottom}
      />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="4 3"
        x1={PLOT.left}
        x2={px}
        y1={py}
        y2={py}
      />
      <circle cx={px} cy={py} fill={ORANGE} r={6} />
      <text
        fill={CHART_INK.text}
        fontSize="14"
        fontStyle="italic"
        textAnchor={px > 380 ? "end" : "start"}
        x={px > 380 ? px - 12 : px + 12}
        y={py - 10}
      >
        {markerText}
      </text>
      <text
        fill={CHART_INK.text}
        fontSize="14"
        fontStyle="italic"
        textAnchor="middle"
        x={(PLOT.left + PLOT.right) / 2}
        y={PLOT.bottom + 40}
      >
        β = v / c
      </text>
      <text
        fill={CHART_INK.text}
        fontSize="14"
        fontStyle="italic"
        textAnchor="start"
        x={PLOT.left + 6}
        y={PLOT.top + 2}
      >
        γ
      </text>
    </ChartSvg>
  );
}

/** Two bars: how long the trip took on Earth and on the traveller's clock. */
export function TripBars({
  id,
  label,
  description,
  earthLabel,
  travellerLabel,
  earthText,
  travellerText,
  ratio,
}: {
  id: string;
  label: string;
  description: string;
  earthLabel: string;
  travellerLabel: string;
  earthText: string;
  travellerText: string;
  /** Traveller time divided by Earth time, 0–1. */
  ratio: number;
}) {
  const full = 330;
  const traveller = Math.max(
    4,
    Math.round(full * Math.min(Math.max(ratio, 0), 1))
  );
  return (
    <ChartSvg
      description={description}
      height={140}
      id={id}
      label={label}
      width={560}
    >
      <text fill={CHART_INK.text} fontSize="14" textAnchor="end" x={142} y={42}>
        {earthLabel}
      </text>
      <rect fill={GREEN} height="30" width={full} x={152} y={22} />
      <text
        fill={CHART_INK.text}
        fontSize="14"
        textAnchor="start"
        x={152 + full + 10}
        y={42}
      >
        {earthText}
      </text>
      <text fill={CHART_INK.text} fontSize="14" textAnchor="end" x={142} y={96}>
        {travellerLabel}
      </text>
      <rect fill={ORANGE} height="30" width={traveller} x={152} y={76} />
      <text
        fill={CHART_INK.text}
        fontSize="14"
        textAnchor="start"
        x={152 + traveller + 10}
        y={96}
      >
        {travellerText}
      </text>
    </ChartSvg>
  );
}
